import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Table,
    Modal,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const MarqueePage = () => {
    const [marquees, setMarquees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editingMarquee, setEditingMarquee] = useState(null);

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [title, setTitle] = useState("");

    const getAuthHeaders = () => {
        const token = localStorage.getItem("authToken");
        return {
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getMarquees = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/marquee`,
                { headers: getAuthHeaders() }
            );

            const data = response.data?.data || response.data || [];
            setMarquees(data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load marquees.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getMarquees();
    }, []);

    const resetForm = () => {
        setTitle("");
        setEditingMarquee(null);
        setErrors({});
        setError("");
    };

    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (marquee) => {
        setEditingMarquee(marquee);
        setTitle(marquee.title || "");
        setErrors({});
        setError("");
        setShowModal(true);
    };

    const closeModal = () => {
        if (!saving) {
            setShowModal(false);
            resetForm();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        if (!title.trim()) {
            setErrors({ title: ["Title is required."] });
            return;
        }

        try {
            setSaving(true);
            setErrors({});

            const payload = { title: title.trim() };

            if (editingMarquee) {
                const response = await axios.put(
                    `${process.env.REACT_APP_API_URL}/web-manager/marquee/${editingMarquee.id}`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                setMessage(response.data?.message || "Marquee updated successfully.");
            } else {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/marquee`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                setMessage(response.data?.message || "Marquee created successfully.");
            }

            setShowModal(false);
            resetForm();
            await getMarquees();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Validation errors");
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    const deleteMarquee = async (id) => {
        if (!window.confirm("Are you sure you want to delete this marquee?")) return;

        try {
            setDeleting(id);
            setError("");
            setMessage("");

            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/marquee/${id}`,
                { headers: getAuthHeaders() }
            );

            setMessage(response.data?.message || "Marquee deleted successfully.");
            await getMarquees();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete marquee.");
        } finally {
            setDeleting(null);
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Marquee Management</h2>
                        <p className="text-muted mb-0">Manage running marquee texts for your website.</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" onClick={openAddModal}>
                            + Add Marquee
                        </Button>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && !showModal && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-3 text-muted">Loading marquees...</div>
                            </div>
                        ) : marquees.length === 0 ? (
                            <div className="text-center py-5">
                                <h5>No marquees found.</h5>
                                <Button variant="primary" className="mt-2" onClick={openAddModal}>
                                    + Add Marquee
                                </Button>
                            </div>
                        ) : (
                            <Table responsive hover className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "80px" }}>#</th>
                                        <th>Title / Content</th>
                                        <th className="text-end" style={{ width: "200px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marquees.map((marquee, index) => (
                                        <tr key={marquee.id || index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <span className="fw-semibold text-dark">
                                                    {marquee.title}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    className="me-2"
                                                    onClick={() => openEditModal(marquee)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    disabled={deleting === marquee.id}
                                                    onClick={() => deleteMarquee(marquee.id)}
                                                >
                                                    {deleting === marquee.id ? "Deleting..." : "Delete"}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                <Modal show={showModal} onHide={closeModal} centered backdrop="static">
                    <Form onSubmit={handleSubmit}>
                        <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                            <Modal.Title className="fw-bold fs-4">
                                {editingMarquee ? "Edit Marquee" : "Add Marquee"}
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="p-4">
                            {error && <Alert variant="danger">{error}</Alert>}
                            <Form.Group>
                                <Form.Label className="fw-bold text-uppercase text-muted small mb-2">
                                    Marquee Title
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={title}
                                    onChange={(e) => {
                                        setTitle(e.target.value);
                                        setErrors((prev) => ({ ...prev, title: "" }));
                                    }}
                                    isInvalid={!!errors.title}
                                    className="bg-light border-0 py-2 px-3"
                                    placeholder="Enter marquee text content"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.title) ? errors.title[0] : errors.title}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Modal.Body>

                        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-end">
                            <Button
                                variant="light"
                                onClick={closeModal}
                                disabled={saving}
                                className="px-4 py-2 text-uppercase fw-semibold bg-light text-secondary border-0 me-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={saving}
                                className="px-4 py-2 text-uppercase fw-semibold"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editingMarquee ? (
                                    "Update Marquee"
                                ) : (
                                    "Add Marquee"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default MarqueePage;